interface Props {
  character: {
    ocean_o: number | null
    ocean_c: number | null
    ocean_e: number | null
    ocean_a: number | null
    ocean_n: number | null
    ocean_archetype: string | null
    personality_text: string | null
    morality: number
    archetype_label: string | null
    life_phase: string | null
    social_class: string
    sex: string
  }
}

// Phase progression per class — ordered from earliest to latest
const CLASS_PHASES: Record<string, string[]> = {
  noble:     ['heir', 'spare', 'lady', 'lord', 'senior_noble'],
  warrior:   ['recruit', 'veteran', 'sergeant', 'senior', 'broken'],
  soldier:   ['recruit', 'established', 'veteran', 'sergeant', 'senior'],
  artisan:   ['apprentice', 'journeyman', 'established', 'senior'],
  merchant:  ['established', 'journeyman', 'senior'],
  peasant:   ['established', 'senior'],
  unguilded: ['established', 'journeyman', 'senior'],
  clergy:    ['postulant', 'ordained', 'senior_clergy'],
}

const PHASE_LABELS: Record<string, string> = {
  heir: 'Heir', spare: 'Spare', lady: 'Lady', lord: 'Lord',
  senior_noble: 'Senior', married_noble: 'Married', widow: 'Widow',
  recruit: 'Recruit', veteran: 'Veteran', sergeant: 'Sergeant',
  senior: 'Senior', broken: 'Broken', established: 'Established',
  apprentice: 'Apprentice', journeyman: 'Journeyman',
  postulant: 'Postulant', ordained: 'Ordained', senior_clergy: 'Senior',
}

const OCEAN_LABELS: Record<string, [string, string]> = {
  O: ['Openness', 'Closed ← → Open'],
  C: ['Conscientiousness', 'Careless ← → Disciplined'],
  E: ['Extroversion', 'Introverted ← → Extroverted'],
  A: ['Agreeableness', 'Callous ← → Compassionate'],
  N: ['Neuroticism', 'Stable ← → Neurotic'],
}

function barColor(dim: string, value: number): string {
  if (dim === 'N') return value > 70 ? 'bg-danger' : value > 30 ? 'bg-warning' : 'bg-success'
  if (dim === 'A') return value > 70 ? 'bg-success' : value > 30 ? 'bg-info' : 'bg-danger'
  return 'bg-accent'
}

export function PersonalityPanel({ character }: Props) {
  const hasOcean = character.ocean_o != null
  const scores: Record<string, number | null> = {
    O: character.ocean_o, C: character.ocean_c, E: character.ocean_e,
    A: character.ocean_a, N: character.ocean_n,
  }

  if (!hasOcean && !character.personality_text) {
    return null // Don't render for manually-created characters without personality data
  }

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">Personality</h3>

      {character.personality_text && (
        <p className="text-sm italic mb-3">{character.personality_text}</p>
      )}

      {character.archetype_label && (
        <div className="text-xs text-ink-light mb-2">
          <span className="font-semibold">Archetype:</span> {character.archetype_label}
        </div>
      )}

      {character.life_phase && (() => {
        const cls = character.social_class?.toLowerCase() ?? ''
        const phases = CLASS_PHASES[cls] ?? []
        const current = character.life_phase
        // If current phase isn't in the standard list, show it standalone
        if (phases.length === 0 || !phases.includes(current)) {
          return (
            <div className="text-xs mb-2">
              <span className="font-semibold">Phase:</span> {PHASE_LABELS[current] ?? current}
            </div>
          )
        }
        return (
          <div className="flex gap-1 mb-2 flex-wrap">
            {phases.map(p => (
              <span key={p} className={`px-2 py-0.5 text-xs rounded ${
                p === current
                  ? 'bg-accent text-white font-bold'
                  : 'bg-parchment text-ink-light'
              }`}>
                {PHASE_LABELS[p] ?? p}
              </span>
            ))}
          </div>
        )
      })()}

      <div className="text-xs mb-3">
        <span className="font-semibold">Morality:</span> {character.morality}
      </div>

      {hasOcean && (
        <div className="space-y-2">
          {Object.entries(OCEAN_LABELS).map(([dim, [name, scale]]) => {
            const val = scores[dim] ?? 50
            return (
              <div key={dim}>
                <div className="flex justify-between text-xs mb-0.5">
                  <span className="font-semibold">{name}</span>
                  <span>{val}</span>
                </div>
                <div className="bg-parchment-dark rounded h-2">
                  <div className={`${barColor(dim, val)} rounded h-2 transition-all`}
                    style={{ width: `${val}%` }} />
                </div>
                <div className="text-xs text-ink-light mt-0.5">{scale}</div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
